import { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import ComicCard from '@/components/feature/ComicCard';
import PageSEO from '@/components/feature/PageSEO';
import { comics, featuredComicIds, hotComicIds, newComicIds, categories } from '@/mocks/comics';
import type { Comic } from '@/mocks/comics';

export default function Home() {
  const [searchQuery, setSearchQuery] = useState('');
  const [currentBanner, setCurrentBanner] = useState(0);

  const featuredComics = useMemo(
    () => featuredComicIds.map((id) => comics.find((c) => c.id === id)).filter(Boolean) as Comic[],
    []
  );
  const hotComics = useMemo(
    () => hotComicIds.map((id) => comics.find((c) => c.id === id)).filter(Boolean) as Comic[],
    []
  );
  const newComics = useMemo(
    () => newComicIds.map((id) => comics.find((c) => c.id === id)).filter(Boolean) as Comic[],
    []
  );

  const filteredComics = useMemo(() => {
    if (!searchQuery.trim()) return comics;
    const q = searchQuery.toLowerCase();
    return comics.filter(
      (c) =>
        c.title.toLowerCase().includes(q) ||
        c.author.toLowerCase().includes(q) ||
        c.tags.some((t) => t.toLowerCase().includes(q))
    );
  }, [searchQuery]);

  const bannerComics = featuredComics.slice(0, 4);

  const banner = bannerComics[currentBanner];

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: '韩漫网',
    url: 'https://douxinkeji.cn',
    description:
      '韩漫网是您的免费韩漫在线阅读平台，提供高清下拉式全集漫画大全，画质清晰阅读流畅，打造影院级观看体验。',
    potentialAction: {
      '@type': 'SearchAction',
      target: 'https://douxinkeji.cn/?search={search_term_string}',
      'query-input': 'required name=search_term_string',
    },
  };

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      <PageSEO
        title="韩漫网_漫画大全_免费韩漫在线阅读平台_高清下拉式韩漫全集"
        description="韩漫网是您的免费韩漫在线阅读平台，提供高清下拉式全集漫画大全，画质清晰阅读流畅，打造影院级观看体验。"
        keywords="韩漫网,漫画大全,免费韩漫,在线阅读平台,高清下拉式全集"
        canonicalUrl="https://douxinkeji.cn/"
      />
      <ScrollToTop />
      <div className="min-h-screen bg-background-50">
        <Navbar onSearchChange={setSearchQuery} />

        {/* Search Results Overlay */}
        {searchQuery.trim() && (
          <div className="px-4 md:px-6 py-8">
            <h2 className="text-lg font-bold font-heading text-foreground-900 mb-4">
              搜索结果：&ldquo;{searchQuery}&rdquo; 共找到 {filteredComics.length} 部漫画
            </h2>
            {filteredComics.length > 0 ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4">
                {filteredComics.map((comic) => (
                  <ComicCard key={comic.id} comic={comic} size="medium" />
                ))}
              </div>
            ) : (
              <p className="text-foreground-500 text-sm">未找到相关漫画，请尝试其他关键词。</p>
            )}
          </div>
        )}

        {!searchQuery.trim() && (
          <>
            {/* Hero Banner */}
            {banner && (
              <section className="relative w-full h-[420px] md:h-[520px] overflow-hidden">
                <img
                  src={banner.coverImage}
                  alt={banner.title}
                  className="w-full h-full object-cover object-top"
                />
                <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-black/10 to-black/40"></div>

                {/* Banner Content */}
                <div className="absolute inset-0 flex items-end">
                  <div className="w-full px-4 md:px-6 pb-10 md:pb-14">
                    <div className="flex flex-col md:flex-row md:items-end gap-4 md:gap-8">
                      {/* Thumbnail */}
                      <Link
                        to={`/comic/${banner.id}`}
                        className="w-[100px] h-[140px] md:w-[130px] md:h-[180px] rounded-lg overflow-hidden shrink-0 cursor-pointer shadow-lg"
                      >
                        <img
                          src={banner.coverImage}
                          alt={banner.title}
                          className="w-full h-full object-cover object-top"
                        />
                      </Link>
                      {/* Info */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-2">
                          {banner.tags.slice(0, 3).map((tag) => (
                            <span
                              key={tag}
                              className="px-2 py-0.5 rounded-full text-xs bg-primary-500/80 text-background-50"
                            >
                              {tag}
                            </span>
                          ))}
                          <span className="px-2 py-0.5 rounded-full text-xs bg-white/20 text-background-50 backdrop-blur-sm">
                            {banner.status}
                          </span>
                        </div>
                        <Link to={`/comic/${banner.id}`} className="cursor-pointer">
                          <h1 className="text-2xl md:text-4xl font-bold font-heading text-background-50 mb-2">
                            {banner.title}
                          </h1>
                        </Link>
                        <p className="text-sm text-background-200 line-clamp-2 max-w-xl mb-3">
                          {banner.description}
                        </p>
                        <div className="flex items-center gap-4 text-sm text-background-300">
                          <span>{banner.author}</span>
                          <span className="flex items-center gap-1">
                            <div className="w-4 h-4 flex items-center justify-center">
                              <i className="ri-star-fill text-xs text-primary-400"></i>
                            </div>
                            {banner.rating}
                          </span>
                          <span>共{banner.totalChapters}话</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Banner Navigation Dots */}
                <div className="absolute bottom-4 right-4 md:right-6 flex items-center gap-2">
                  {bannerComics.map((_, i) => (
                    <button
                      key={i}
                      onClick={() => setCurrentBanner(i)}
                      className={`w-2 h-2 rounded-full transition-all cursor-pointer ${
                        i === currentBanner
                          ? 'bg-primary-500 w-6'
                          : 'bg-background-50/50 hover:bg-background-50/80'
                      }`}
                      aria-label={`切换到第${i + 1}个横幅`}
                    />
                  ))}
                </div>

                {/* Banner Arrows */}
                <button
                  onClick={() =>
                    setCurrentBanner((prev) =>
                      prev === 0 ? bannerComics.length - 1 : prev - 1
                    )
                  }
                  className="absolute left-3 md:left-6 top-1/2 -translate-y-1/2 w-8 h-8 md:w-10 md:h-10 flex items-center justify-center rounded-full bg-foreground-950/40 text-background-50 hover:bg-foreground-950/60 transition-all cursor-pointer"
                  aria-label="上一个"
                >
                  <i className="ri-arrow-left-s-line text-lg"></i>
                </button>
                <button
                  onClick={() =>
                    setCurrentBanner((prev) =>
                      prev === bannerComics.length - 1 ? 0 : prev + 1
                    )
                  }
                  className="absolute right-3 md:right-6 top-1/2 -translate-y-1/2 w-8 h-8 md:w-10 md:h-10 flex items-center justify-center rounded-full bg-foreground-950/40 text-background-50 hover:bg-foreground-950/60 transition-all cursor-pointer"
                  aria-label="下一个"
                >
                  <i className="ri-arrow-right-s-line text-lg"></i>
                </button>
              </section>
            )}

            {/* Category Navigation */}
            <section className="px-4 md:px-6 py-6 bg-background-100">
              <div className="flex items-center gap-3 overflow-x-auto pb-2 scrollbar-none flex-wrap">
                {categories.map((cat) => (
                  <Link
                    key={cat.slug}
                    to={`/category/${cat.slug}`}
                    className="flex items-center gap-1.5 px-4 py-2 rounded-full bg-background-50 text-sm text-foreground-700 hover:bg-primary-500 hover:text-background-50 transition-all cursor-pointer whitespace-nowrap shrink-0"
                  >
                    <div className="w-4 h-4 flex items-center justify-center">
                      <i className={`${cat.icon} text-sm`}></i>
                    </div>
                    <span>{cat.name}</span>
                  </Link>
                ))}
              </div>
            </section>

            {/* Featured Comics */}
            <section className="px-4 md:px-6 py-10 md:py-14">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl md:text-2xl font-bold font-heading text-foreground-900">
                  <strong>编辑精选</strong>
                </h2>
                <Link
                  to="/category/all"
                  className="text-sm text-primary-500 hover:text-primary-600 transition-colors flex items-center gap-1 cursor-pointer whitespace-nowrap"
                >
                  查看全部
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-arrow-right-line text-xs"></i>
                  </div>
                </Link>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4 md:gap-5">
                {featuredComics.map((comic) => (
                  <ComicCard key={comic.id} comic={comic} size="large" />
                ))}
              </div>
            </section>

            {/* Hot Comics */}
            <section className="px-4 md:px-6 py-10 md:py-14 bg-background-100">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl md:text-2xl font-bold font-heading text-foreground-900">
                  <strong>热门排行</strong>
                </h2>
                <Link
                  to="/category/all"
                  className="text-sm text-primary-500 hover:text-primary-600 transition-colors flex items-center gap-1 cursor-pointer whitespace-nowrap"
                >
                  查看全部
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-arrow-right-line text-xs"></i>
                  </div>
                </Link>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4 md:gap-5">
                {hotComics.map((comic, index) => (
                  <div key={comic.id} className="relative">
                    <div className="absolute -top-2 -left-2 w-7 h-7 rounded-full bg-primary-500 text-background-50 flex items-center justify-center text-xs font-bold z-10">
                      {index + 1}
                    </div>
                    <ComicCard comic={comic} size="medium" />
                  </div>
                ))}
              </div>
            </section>

            {/* New Releases */}
            <section className="px-4 md:px-6 py-10 md:py-14">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl md:text-2xl font-bold font-heading text-foreground-900">
                  <strong>最新上架</strong>
                </h2>
                <Link
                  to="/category/all"
                  className="text-sm text-primary-500 hover:text-primary-600 transition-colors flex items-center gap-1 cursor-pointer whitespace-nowrap"
                >
                  查看全部
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-arrow-right-line text-xs"></i>
                  </div>
                </Link>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4 md:gap-5">
                {newComics.map((comic) => (
                  <ComicCard key={comic.id} comic={comic} size="medium" />
                ))}
              </div>
            </section>

            {/* Platform Features */}
            <section className="px-4 md:px-6 py-10 md:py-14 bg-background-100">
              <h2 className="text-xl md:text-2xl font-bold font-heading text-foreground-900 mb-8 text-center">
                <strong>为什么选择韩漫网</strong>
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                {[
                  {
                    icon: 'ri-smartphone-line',
                    title: '高清画质',
                    desc: '提供超清画质的下拉式阅读体验，支持多种设备完美适配',
                  },
                  {
                    icon: 'ri-flashlight-line',
                    title: '极速加载',
                    desc: '先进的图片压缩技术，确保漫画秒开不卡顿',
                  },
                  {
                    icon: 'ri-global-line',
                    title: '海量资源',
                    desc: '收录数千部热门韩漫，涵盖恋爱、奇幻、热血等多种题材',
                  },
                  {
                    icon: 'ri-shield-check-line',
                    title: '完全免费',
                    desc: '所有漫画完全免费阅读，无需付费订阅',
                  },
                ].map((item, i) => (
                  <div
                    key={i}
                    className="text-center p-6 rounded-lg bg-background-50"
                  >
                    <div className="w-12 h-12 mx-auto mb-3 flex items-center justify-center rounded-full bg-primary-100">
                      <i className={`${item.icon} text-xl text-primary-500`}></i>
                    </div>
                    <h3 className="text-base font-bold font-heading text-foreground-900 mb-2">
                      {item.title}
                    </h3>
                    <p className="text-sm text-foreground-600 leading-relaxed">
                      {item.desc}
                    </p>
                  </div>
                ))}
              </div>
            </section>

            {/* FAQ Schema */}
            <section className="px-4 md:px-6 py-10 md:py-14">
              <h2 className="text-xl md:text-2xl font-bold font-heading text-foreground-900 mb-8 text-center">
                <strong>常见问题</strong>
              </h2>
              <div className="max-w-3xl mx-auto space-y-3">
                {[
                  {
                    q: '韩漫网真的完全免费吗？',
                    a: '是的，韩漫网所有漫画内容完全免费，无需付费订阅。您可以在线阅读所有连载和完结漫画，享受无限制的阅读体验。',
                  },
                  {
                    q: '韩漫网支持手机阅读吗？',
                    a: '当然支持！韩漫网采用响应式设计，完美适配手机、平板和电脑等多种设备。下拉式阅读方式特别适合手机端阅读，您可以随时随地畅享韩漫。',
                  },
                  {
                    q: '韩漫网更新速度快吗？',
                    a: '我们与各大漫画平台保持同步更新，热门连载漫画通常会在官方更新后24小时内同步上线，确保您不错过任何精彩内容。',
                  },
                  {
                    q: '如何找到想看的漫画？',
                    a: '您可以通过顶部的搜索框搜索漫画名称、作者或标签，也可以通过分类导航浏览不同类型的漫画。首页还提供了编辑精选、热门排行等推荐栏目。',
                  },
                ].map((faq, i) => (
                  <details
                    key={i}
                    className="group bg-background-50 rounded-lg border border-background-200 overflow-hidden"
                  >
                    <summary className="flex items-center justify-between px-5 py-4 cursor-pointer hover:bg-background-100 transition-colors">
                      <span className="text-sm font-medium text-foreground-800 pr-4">
                        {faq.q}
                      </span>
                      <div className="w-5 h-5 flex items-center justify-center shrink-0">
                        <i className="ri-arrow-down-s-line text-foreground-500 group-open:rotate-180 transition-transform"></i>
                      </div>
                    </summary>
                    <div className="px-5 pb-4">
                      <p className="text-sm text-foreground-600 leading-relaxed">
                        {faq.a}
                      </p>
                    </div>
                  </details>
                ))}
              </div>
              <script
                type="application/ld+json"
                dangerouslySetInnerHTML={{
                  __html: JSON.stringify({
                    '@context': 'https://schema.org',
                    '@type': 'FAQPage',
                    mainEntity: [
                      {
                        q: '韩漫网真的完全免费吗？',
                        a: '是的，韩漫网所有漫画内容完全免费，无需付费订阅。您可以在线阅读所有连载和完结漫画，享受无限制的阅读体验。',
                      },
                      {
                        q: '韩漫网支持手机阅读吗？',
                        a: '当然支持！韩漫网采用响应式设计，完美适配手机、平板和电脑等多种设备。下拉式阅读方式特别适合手机端阅读，您可以随时随地畅享韩漫。',
                      },
                      {
                        q: '韩漫网更新速度快吗？',
                        a: '我们与各大漫画平台保持同步更新，热门连载漫画通常会在官方更新后24小时内同步上线，确保您不错过任何精彩内容。',
                      },
                      {
                        q: '如何找到想看的漫画？',
                        a: '您可以通过顶部的搜索框搜索漫画名称、作者或标签，也可以通过分类导航浏览不同类型的漫画。首页还提供了编辑精选、热门排行等推荐栏目。',
                      },
                    ].map((item) => ({
                      '@type': 'Question',
                      name: item.q,
                      acceptedAnswer: {
                        '@type': 'Answer',
                        text: item.a,
                      },
                    })),
                  }),
                }}
              />
            </section>
          </>
        )}

        <Footer />
      </div>
    </>
  );
}